import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Accordion, BodyText, Flex, Icon, Link, List } from '@gaiads/telia-react-component-library';

import { getMainSiteNavigationComponentHeight } from '../../features/App/Header/Header';
import { HTMLElementProps } from '../../utils/types';

export interface SectionsProps {
  sectionTitles: {
    id: string;
    title: string;
    sectionHasErrors: boolean;
  }[];
  sectionsRef: React.MutableRefObject<(HTMLDivElement | null)[]>;
  scrollOnHash?: boolean;
  accordionProps?: Partial<React.ComponentProps<typeof Accordion> & HTMLElementProps>;
}
export default function Sections({ sectionTitles, sectionsRef, scrollOnHash, accordionProps }: SectionsProps) {
  const { t } = useTranslation('sections');
  const history = useHistory();

  const [activeSectionTab, setActiveSectionTab] = useState<string>(sectionTitles[0].id);

  const scrollToSection = (ref: HTMLDivElement | null) => {
    if (ref) {
      const pos = ref.offsetTop - getMainSiteNavigationComponentHeight();
      window.scrollTo({ top: pos, behavior: 'smooth' });
    }
  };
  const handleSectionChange = (id: string) => {
    const index = sectionTitles.findIndex(section => section.id === id);
    if (index !== -1) {
      setActiveSectionTab(id);
      scrollToSection(sectionsRef.current[index]);
    }
  };

  history.listen(({ hash }) => {
    const id = hash.replace('#', '');
    if (id && id !== activeSectionTab) {
      handleSectionChange(id);
    }
  });

  useEffect(() => {
    if (scrollOnHash) {
      const { hash } = window.location;
      const id = hash.replace('#', '');
      handleSectionChange(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollOnHash, window.location]);

  return (
    <Accordion heading={t('title')} initiallyExpanded={true} {...accordionProps}>
      <List numbered={false} disc={false}>
        {sectionTitles.map(({ id, title, sectionHasErrors }) => (
          <List.Item key={title} margin={{ bottom: 'md' }}>
            <Flex>
              <Link to={`#${id}`} className={id === activeSectionTab && 'product_form_sections_nav_list__active-tab'} block>
                <BodyText color="black" fontWeight={id === activeSectionTab ? 'bold' : 'normal'} hoverColor="purple500" clickableAppearance block>
                  {title}
                </BodyText>
              </Link>
              {sectionHasErrors && (
                <Icon
                  data-testid={`section-${title}-has-errors-indicator`}
                  name="alert-filled"
                  size="md"
                  color="red400"
                  margin={{ left: 'sm' }}
                  title={t('editscreen.section_has_errors_tooltip')}
                />
              )}
            </Flex>
          </List.Item>
        ))}
      </List>
    </Accordion>
  );
}
